import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useFormikContext } from 'formik';

import FieldImageDragDrop from '~/app/components/forms/FieldImageDragDrop.jsx';
import FieldSentenceCompletion from '~/app/components/forms/FieldSentenceCompletion.jsx';
import FieldIdiomFill from '~/app/components/forms/FieldIdiomFill.jsx';

import FormCheckGroup from '~/commons/components/FormCheckGroup.jsx';
import NewLines from '~/commons/components/Newlines.jsx';
import CachedVideo from '~/commons/components/CachedVideo.jsx';
import { numToLetter } from '~/commons/helpers';
import { isEqualWith } from 'lodash';
import Padding from '../layout/Padding.jsx';

import FieldsSingleSelect from './FieldsSingleSelect.jsx';
import FieldsMultiSelect from './FieldsMultiSelect.jsx';
import FieldsOrdering from './FieldsOrdering.jsx';
import FieldsMatching from './FieldsMatching.jsx';

const formatFields = {
  'single-select': FieldsSingleSelect,
  'multi-select': FieldsMultiSelect,
  ordering: FieldsOrdering,
  matching: FieldsMatching,
  'image-dragdrop': ({
    qtnIndex,
    question,
    disabled,
    showAns,
    resetSignal,
    showReset,
  }) => (
    <FieldImageDragDrop
      question={question}
      qIndex={qtnIndex}
      disabled={disabled}
      showAns={showAns}
      resetSignal={resetSignal}
      showReset={showReset}
    />
  ),
  'sentence-completion': ({
    qtnIndex,
    question,
    disabled,
    showAns,
    resetSignal,
    showReset,
  }) => (
    <FieldSentenceCompletion
      question={question}
      qIndex={qtnIndex}
      disabled={disabled}
      showAns={showAns}
      resetSignal={resetSignal}
      showReset={showReset}
    />
  ),
  'idiom-fill': ({
    qtnIndex,
    question,
    disabled,
    showAns,
    resetSignal,
    showReset,
  }) => (
    <FieldIdiomFill
      question={question}
      qIndex={qtnIndex}
      disabled={disabled}
      showAns={showAns}
      resetSignal={resetSignal}
      showReset={showReset}
    />
  ),
};

const FieldsQuestions = forwardRef(
  (
    {
      answer,
      questions,
      numChar = (i) => `${i + 1}.`,
      showAns = false,
      doublePoint,
      setDoublePoint,
    },
    ref,
  ) => {
    const { values, setFieldValue } = useFormikContext();
    const qRefs = useRef({});
    const [resetSignals, setResetSignals] = useState({});
    console.log({ questions });
    useImperativeHandle(ref, () => ({
      scrollIntoView: (_id) => {
        qRefs.current[_id].scrollIntoView({ block: 'center' });
      },
    }));
    return (
      <Padding className="readable">
        {questions.map((q, i) => {
          const { answered, answeredSeq } = values.answers[i];
          const { format = 'single-select' } = q;
          const FieldAns = formatFields[format];
          const challenge = !!q.challenge;
          // Per-format Field components (encapsulate adapters + runtime + Formik mapping)
          let correctAns;
          let isCorrect;

          // get correct answer
          if (answer || showAns) {
            correctAns = format === 'single-select'
              ? q?.answer?.toString()
              : q?.answers?.map((a) => a.toString());

            if (format === 'single-select') {
              isCorrect = answered === correctAns;
            } else if (format === 'multi-select') {
              isCorrect = isEqualWith(
                answeredSeq.sort(),
                correctAns.sort(),
                (a, b) => a.toString() === b.toString(),
              );
            } else {
              isCorrect = isEqualWith(
                answeredSeq,
                correctAns,
                (a, b) => a.toString() === b.toString(),
              );
            }
          }

          const isDouble = doublePoint?._id === q._id;

          return (
            <div
              key={q._id}
              className="card my-4"
              ref={(el) => {
                qRefs.current[q._id] = el;
              }}
              style={{
                background: challenge ? '#FBEDFF' : undefined,
                border: challenge ? '1px solid #E4E4E4' : undefined,
                boxShadow: isDouble
                  ? '0 0 0 3px rgba(255,122,122,0.12)'
                  : undefined,
              }}
            >
              <div className="card-body">
                {challenge && (
                  <div className="d-flex align-items-center mb-3 flex-column flex-md-row">
                    <div className="mr-3 mb-2 mb-md-0">
                      <img
                        src={'assets/img/challenge.svg'}
                        alt="挑戰題"
                        className="img-fluid"
                      />
                    </div>

                    <div className="d-flex align-items-center flex-column flex-md-row ml-md-auto">
                      <button
                        type="button"
                        className="btn btn-link p-0 mb-2 mb-md-0 mr-md-2"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (isDouble) {
                            setDoublePoint(null);
                          } else {
                            setDoublePoint({
                              ...q,
                              quesationNum: i + 1,
                            });
                          }
                        }}
                      >
                        <img
                          src={
                            isDouble
                              ? 'assets/img/doublePoint_active.svg'
                              : 'assets/img/doublePoint.svg'
                          }
                          alt={isDouble ? '已選為雙倍' : '選為雙倍'}
                          className="img-fluid"
                        />
                      </button>
                      <button
                        type="button"
                        className="btn btn-link p-0 mb-2 mb-md-0"
                        onClick={() => {
                          // const next = { ...resetSignals };
                          // questions.forEach((qq) => {
                          //   if (
                          //     [
                          //       'image-dragdrop',
                          //       'sentence-completion',
                          //       'idiom-fill',
                          //     ].includes(qq.format)
                          //   ) {
                          //     next[qq._id] = (next[qq._id] || 0) + 1;
                          //   }
                          // });
                          // setResetSignals(next);
                          // console.log('reset', next, q._id);
                          setResetSignals({[q._id]: Date.now()});
                        }}
                      >
                        <img src={'assets/img/rewrite.svg'} alt="重設" className="img-fluid" />
                      </button>
                    </div>
                  </div>
                )}
                <div className="row text-secondary">
                  <h5 className="col-auto pr-0 question-num">{numChar(i)}</h5>
                  <h5 className="col pl-2 question-text">
                    <NewLines>{q.question}</NewLines>
                  </h5>
                </div>
                {q.img && (
                  <>
                    {q.img.split('.').pop().toLowerCase() == 'mp4' ? (
                      <div className="py-3 text-center question-video">
                        <CachedVideo
                          src={q.img}
                          controls
                          disablePictureInPicture={true}
                          controlslist="nodownload noplaybackrate"
                        />
                      </div>
                    ) : (
                      <div className="py-3 text-center question-image">
                        <img src={q.img} />
                      </div>
                    )}
                  </>
                )}
                {/* answer */}
                {FieldAns ? (
                  <FieldAns
                    qtnIndex={i}
                    question={q}
                    answered={answered}
                    answeredSeq={answeredSeq}
                    isCorrect={isCorrect}
                    correctAns={correctAns}
                    disabled={answer}
                    resetSignal={resetSignals[q._id]}
                    showReset={false}
                  />
                ) : (
                  <div className="text-danger">
                    Unsupported question format: {String(format)}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </Padding>
    );
  },
);

FieldsQuestions.displayName = 'FieldsQuestions';

export default FieldsQuestions;
